import { useCallback, useEffect, useRef, useState } from 'react';

export type PaymentCopyField = 'phone' | 'card' | 'bank' | 'name' | 'mobile_operator' | 'amount';
export type PaymentCopyFeedback = { field: PaymentCopyField; status: 'success' | 'error'; id: number };

export function usePaymentCopyFeedback() {
    const [feedback, setFeedback] = useState<PaymentCopyFeedback | null>(null);
    const request = useRef(0);
    const mounted = useRef(true);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            request.current += 1;
            clearTimeout(timer.current);
        };
    }, []);

    const copy = useCallback(async (text: string, field: PaymentCopyField) => {
        const id = ++request.current;
        clearTimeout(timer.current);
        setFeedback(null);
        let success = false;
        try {
            await navigator.clipboard.writeText(text);
            success = true;
        } catch {
            // Missing Clipboard API and permission failures use the same manual-copy hint.
        }
        if (mounted.current && request.current === id) {
            setFeedback({ field, status: success ? 'success' : 'error', id });
            timer.current = setTimeout(() => setFeedback(null), success ? 2000 : 4000);
        }
        return success;
    }, []);

    return { copy, feedback, copiedField: feedback?.status === 'success' ? feedback.field : null };
}
